import { useEffect, useState } from "react";
import { Wallet, TrendingUp, Receipt, ShoppingBag, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { api } from "../lib/api";
import { rp, rangeFor, tzOffset, METHODS, downloadCSV, todayWIB } from "../lib/format";
import { StatCard, PageWrap } from "../components/common";
import { DateFilter } from "../components/DateFilter";
import { ChartTip } from "./Dashboard";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const PIE = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))", "#06B6D4"];
const today = todayWIB();
const axis = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };

const Card = ({ title, children, className = "" }) => (
  <div className={`rounded-2xl border bg-card p-5 ${className}`}>
    <h2 className="font-display font-bold mb-4">{title}</h2>
    {children}
  </div>
);

export default function Reports() {
  const [preset, setPreset] = useState("7d");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/reports/summary", { params: { ...rangeFor(preset, from, to), tz_offset: tzOffset() } }).then((r) => setD(r.data));
  }, [preset, from, to]);

  const single = preset === "today" || preset === "yesterday";
  const chartData = single
    ? (d?.hourly || []).filter((h) => h.revenue > 0 || (parseInt(h.hour, 10) >= 7 && parseInt(h.hour, 10) <= 22)).map((h) => ({ label: h.hour, revenue: h.revenue }))
    : (d?.daily || []).map((x) => ({ ...x, label: new Date(x.date + "T00:00:00").toLocaleDateString("id-ID", { day: "numeric", month: "short" }) }));
  const pie = (d?.by_method || []).map((m) => ({ name: METHODS[m.method] || m.method, value: m.total }));

  const exportCsv = () =>
    downloadCSV("laporan-penjualan.csv", [["Tanggal", "Jumlah Transaksi", "Penjualan", "Laba Kotor"], ...(d?.daily || []).map((x) => [x.date, x.count, x.revenue, x.profit]),
      [], ["Produk Terlaris", "Qty", "Penjualan"], ...(d?.top_products || []).map((p) => [p.name, p.qty, p.revenue])]);

  return (
    <PageWrap>
      <div className="flex flex-wrap gap-3 justify-between items-center">
        <DateFilter {...{ preset, setPreset, from, setFrom, to, setTo }} />
        <Button variant="outline" onClick={exportCsv} data-testid="report-export-btn"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard testid="report-revenue" icon={Wallet} label="Total Penjualan" value={rp(d?.revenue)} />
        <StatCard testid="report-profit" icon={TrendingUp} tone="success" label="Laba Kotor" value={rp(d?.profit)} hint={d?.revenue ? `Margin ${((d.profit / d.revenue) * 100).toFixed(1)}%` : ""} />
        <StatCard testid="report-count" icon={Receipt} tone="chart-5" label="Transaksi" value={d?.count ?? 0} hint={`Rata-rata ${rp(d?.avg_ticket)}`} />
        <StatCard testid="report-items" icon={ShoppingBag} tone="warning" label="Item Terjual" value={d?.items_sold ?? 0} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card title={single ? "Penjualan per Jam" : "Penjualan Harian"} className="xl:col-span-2">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => (v / 1000).toFixed(0) + "k"} tick={axis} axisLine={false} tickLine={false} width={44} />
                <Tooltip content={<ChartTip />} cursor={{ fill: "hsl(var(--accent))" }} />
                <Bar dataKey="revenue" name="Penjualan" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Metode Pembayaran">
          <div className="h-72">
            {pie.length ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                    {pie.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} stroke="none" />)}
                  </Pie>
                  <Tooltip formatter={(v) => rp(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground">Belum ada data.</p>}
          </div>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Penjualan per Kasir">
          <div className="space-y-2" data-testid="report-by-cashier">
            {(d?.by_cashier || []).map((c) => (
              <div key={c.cashier_id} className="flex justify-between text-sm rounded-lg bg-muted/50 px-3 py-2"><span>{c.name} <span className="text-muted-foreground">({c.count} trx)</span></span><span className="font-mono font-semibold">{rp(c.total)}</span></div>
            ))}
            {d && !d.by_cashier?.length && <p className="text-sm text-muted-foreground">Belum ada data.</p>}
          </div>
        </Card>
        <Card title="Diskon & Member">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-muted/50 p-4"><div className="text-xs text-muted-foreground">Total Diskon Diberikan</div><div className="font-mono font-bold text-lg text-destructive" data-testid="report-discount-total">{rp(d?.discount_total)}</div></div>
            <div className="rounded-xl bg-muted/50 p-4"><div className="text-xs text-muted-foreground">Penjualan ke Member</div><div className="font-mono font-bold text-lg text-primary" data-testid="report-member-sales">{rp(d?.member_sales)}</div></div>
          </div>
        </Card>
      </div>
      <Card title="Produk Terlaris">
        <div className="overflow-x-auto">
          <Table data-testid="top-products-table">
            <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Produk</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Penjualan</TableHead></TableRow></TableHeader>
            <TableBody>
              {(d?.top_products || []).map((p, i) => (
                <TableRow key={p.product_id}>
                  <TableCell className="font-mono text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-right font-mono">{p.qty}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{rp(p.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </PageWrap>
  );
}
